import { ActiveTab, Tenant, User } from '../types'

interface Props {
  activeTab: ActiveTab
  onSelectTab: (tab: ActiveTab) => void
  tenant: Tenant | null
  user: User | null
  onLogout: () => void
  counts: {
    products: number
    tradingGoods: number
    semiFinished: number
    rawMaterials: number
    packagings: number
    categories: number
    salesChannels: number
    demoProducts?: number
    demoSemiFinished?: number
  }
}

export function Sidebar({
  activeTab,
  onSelectTab,
  tenant,
  user,
  onLogout,
  counts,
}: Props) {
  const userInitials = user?.full_name
    ? user.full_name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : user?.email
    ? user.email.slice(0, 2).toUpperCase()
    : 'U'

  return (
    <aside className="app-sidebar no-print" aria-label="Ana Menü">
      {/* Workspace Brand Block */}
      <div className="sidebar-brand-block">
        <div className="brand-icon-wrapper" aria-hidden="true">
          {tenant?.logo_data ? (
            <img src={tenant.logo_data} alt={tenant.name || 'Logo'} className="brand-logo-img" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          )}
        </div>
        <div className="brand-text-wrapper">
          <span className="brand-name" title={tenant?.name || 'Tenant'}>
            {tenant?.name || 'Firma Hesabı'}
          </span>
          <div className="tenant-status-row">
            <span className="tenant-display-name">Cost ERP</span>
          </div>
        </div>
      </div>

      {/* Navigation Sections */}
      <nav className="sidebar-nav">
        {/* Section: Pricing */}
        <div className="nav-group">
          <div className="nav-group-title">Fiyatlandırma & Raporlar</div>
          <button
            type="button"
            className={`nav-item ${activeTab === 'price-list' ? 'active' : ''}`}
            onClick={() => onSelectTab('price-list')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </span>
            <span className="nav-label">Fiyat Listeleri</span>
          </button>
        </div>

        {/* Section: Catalog */}
        <div className="nav-group">
          <div className="nav-group-title">Ürün Kataloğu</div>
          <button
            type="button"
            className={`nav-item ${activeTab === 'products' ? 'active' : ''}`}
            onClick={() => onSelectTab('products')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="m21 7.5-9-5.25L3 7.5m18 0-9 5.25m9-5.25v9l-9 5.25M3 7.5l9 5.25M3 7.5v9l9 5.25m0-9v9" />
              </svg>
            </span>
            <span className="nav-label">Mamul Ürünler</span>
            <span className="nav-count">{counts.products}</span>
          </button>

          <button
            type="button"
            className={`nav-item ${activeTab === 'trading-goods' ? 'active' : ''}`}
            onClick={() => onSelectTab('trading-goods')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5" />
              </svg>
            </span>
            <span className="nav-label">Al-Sat Ürünler</span>
            <span className="nav-count">{counts.tradingGoods}</span>
          </button>

          <button
            type="button"
            className={`nav-item ${activeTab === 'semi-finished' ? 'active' : ''}`}
            onClick={() => onSelectTab('semi-finished')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </span>
            <span className="nav-label">Yarı Mamuller</span>
            <span className="nav-count">{counts.semiFinished}</span>
          </button>
        </div>

        {/* Section: Demo */}
        <div className="nav-group">
          <div className="nav-group-title">Demo Fiyat Hesaplama</div>
          <button
            type="button"
            className={`nav-item ${activeTab === 'demo-products' ? 'active' : ''}`}
            onClick={() => onSelectTab('demo-products')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </span>
            <span className="nav-label">Demo Ürünler</span>
            <span className="nav-count">{counts.demoProducts ?? 0}</span>
          </button>

          <button
            type="button"
            className={`nav-item ${activeTab === 'demo-semi-finished' ? 'active' : ''}`}
            onClick={() => onSelectTab('demo-semi-finished')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </span>
            <span className="nav-label">Demo Yarı Mamuller</span>
            <span className="nav-count">{counts.demoSemiFinished ?? 0}</span>
          </button>

          <button
            type="button"
            className={`nav-item ${activeTab === 'demo-price-list' ? 'active' : ''}`}
            onClick={() => onSelectTab('demo-price-list')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
            </span>
            <span className="nav-label">Demo Fiyat Listeleri</span>
          </button>
        </div>

        {/* Section: Components */}
        <div className="nav-group">
          <div className="nav-group-title">Malzeme & Bileşenler</div>
          <button
            type="button"
            className={`nav-item ${activeTab === 'raw-materials' ? 'active' : ''}`}
            onClick={() => onSelectTab('raw-materials')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <path d="M3 9h18M9 21V9" />
              </svg>
            </span>
            <span className="nav-label">Hammaddeler</span>
            <span className="nav-count">{counts.rawMaterials}</span>
          </button>

          <button
            type="button"
            className={`nav-item ${activeTab === 'packagings' ? 'active' : ''}`}
            onClick={() => onSelectTab('packagings')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </span>
            <span className="nav-label">Ambalajlar</span>
            <span className="nav-count">{counts.packagings}</span>
          </button>
        </div>

        {/* Section: Parameters */}
        <div className="nav-group">
          <div className="nav-group-title">Sistem & Parametreler</div>
          <button
            type="button"
            className={`nav-item ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => onSelectTab('categories')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
            </span>
            <span className="nav-label">Kategoriler</span>
            <span className="nav-count">{counts.categories}</span>
          </button>

          <button
            type="button"
            className={`nav-item ${activeTab === 'sales-channels' ? 'active' : ''}`}
            onClick={() => onSelectTab('sales-channels')}
          >
            <span className="nav-icon" aria-hidden="true">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </span>
            <span className="nav-label">Satış Kanalları</span>
            <span className="nav-count">{counts.salesChannels}</span>
          </button>

          {user?.role === 'tenant_admin' && (
            <button
              type="button"
              className={`nav-item ${activeTab === 'settings' ? 'active' : ''}`}
              onClick={() => onSelectTab('settings')}
            >
              <span className="nav-icon" aria-hidden="true">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </span>
              <span className="nav-label">Firma Ayarları</span>
            </button>
          )}
        </div>
      </nav>

      {/* User Profile Card Footer */}
      <div className="sidebar-footer">
        <div className="user-profile-card">
          <div className="user-avatar" aria-hidden="true">
            {userInitials}
          </div>
          <div className="user-info-text">
            <span className="user-name" title={user?.full_name || user?.email || 'Kullanıcı'}>
              {user?.full_name || user?.email?.split('@')[0] || 'Kullanıcı'}
            </span>
            <span className="user-role-label">
              {user?.role === 'tenant_admin'
                ? 'Tenant Yöneticisi'
                : 'Kullanıcı'}
            </span>
          </div>
          <button
            type="button"
            className="btn-logout"
            onClick={onLogout}
            title="Oturumu Kapat"
            aria-label="Oturumu Kapat"
          >
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>
    </aside>
  )
}
